/**
 * Logo di un titolo accanto al nome (F-186): serve a riconoscere la riga a
 * colpo d'occhio, non a identificarla — quindi è decorativo (`aria-hidden`),
 * il nome resta il testo.
 *
 * Il posto è sempre occupato, logo o no: finché il logo non arriva, o se non
 * esiste, c'è l'iniziale su un tondo di tinta ricavata dal nome. Così le righe
 * non saltano al caricamento e un titolo senza logo ha comunque il suo colore,
 * sempre lo stesso.
 */

import { useState, type CSSProperties } from 'react';
import { instrumentLogoUrl } from './api';
import './instrument-logo.css';

/** Tinta stabile dal nome: lo stesso titolo ha lo stesso colore in ogni pagina. */
function hueOf(name: string): number {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) % 360;
  return hash;
}

function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

export function InstrumentLogo({
  instrumentId,
  name,
  size = 'sm',
}: {
  instrumentId: string;
  name: string;
  size?: 'sm' | 'md';
}) {
  // Esiti per id e non booleani: la stessa istanza può passare a un altro titolo.
  const [loaded, setLoaded] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const isLoaded = loaded === instrumentId;
  const isFailed = failed === instrumentId;

  return (
    <span
      className={`ilogo ilogo--${size}${isLoaded ? ' ilogo--loaded' : ''}`}
      style={{ '--ilogo-hue': hueOf(name) } as CSSProperties}
      aria-hidden="true"
      data-testid="instrument-logo"
    >
      {!isLoaded && <span className="ilogo__initial">{initialOf(name)}</span>}
      {!isFailed && (
        <img
          className="ilogo__img"
          src={instrumentLogoUrl(instrumentId)}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(instrumentId)}
          onError={() => setFailed(instrumentId)}
        />
      )}
    </span>
  );
}
